/**
 * Проекты (§3.1): вкладки «Активные» / «Архив», список широких строк — название, рубрика, описание,
 * плашка и хэштеги, счётчики постов, действия справа. Общий проект «Импульса» нельзя удалить или архивировать.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router";
import { Archive, ArchiveRestore, ChevronDown, PenLine, Pencil, Plus, Sparkles, Trash2 } from "lucide-react";
import type { PostFull, Project } from "@impuls/shared";
import { api, ApiError, errText } from "../lib/api";
import { countText } from "../lib/format";
import { useProjects, useRefreshPosts } from "../lib/queries";
import { EcgLoader } from "../components/Ecg";
import { Tabs } from "../components/PostBits";
import { ProjectsFormModal } from "../components/ProjectsForm";
import { ProjectsMenu, ProjectsRichText, ProjectsVkText, projectsExcerpt, projectsHasStructure } from "../components/ProjectsBits";
import { EmptyState, LoadError, PageHeader, useConfirm, useToast } from "../components/ui";

type TabValue = "active" | "archive";

export function ProjectsPage() {
  const [params, setParams] = useSearchParams();
  const tab: TabValue = params.get("tab") === "archive" ? "archive" : "active";
  const active = useProjects("0");
  const archived = useProjects("1");
  const q = tab === "archive" ? archived : active;
  const [form, setForm] = useState<{ project: Project | null } | null>(null);

  // /projects?new=1 — сразу открыть создание (для ссылок из других разделов)
  useEffect(() => {
    if (params.get("new") === "1") {
      setForm({ project: null });
      const next = new URLSearchParams(params);
      next.delete("new");
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  const setTab = (t: TabValue) => setParams(t === "archive" ? { tab: "archive" } : {}, { replace: true });
  const create = () => setForm({ project: null });
  const list = q.data ?? [];

  return (
    <div className="page proj-page">
      <PageHeader
        title="Проекты"
        actions={
          <button className="btn btn--primary" onClick={create}>
            <Plus size={18} aria-hidden="true" />
            Новый проект
          </button>
        }
      />

      <div className="proj-toolbar">
        <Tabs<TabValue>
          label="Проекты"
          value={tab}
          onChange={setTab}
          tabs={[
            { value: "active", label: "Активные" },
            { value: "archive", label: "Архив" },
          ]}
        />
        {q.data && list.length > 0 && (
          <span className="proj-toolbar__count">{countText(list.length, "проект", "проекта", "проектов")}</span>
        )}
      </div>

      <div role="tabpanel" aria-label={tab === "archive" ? "Архив" : "Активные проекты"}>
        {q.isLoading ? (
          <div className="proj-loading">
            <EcgLoader label="Загружаю проекты" />
          </div>
        ) : q.error ? (
          <LoadError error={q.error} retry={() => void q.refetch()} />
        ) : list.length === 0 ? (
          tab === "archive" ? (
            <EmptyState
              mascot="hug"
              title="Архив пуст"
              action={
                <button className="btn" onClick={() => setTab("active")}>
                  К активным проектам
                </button>
              }
            />
          ) : (
            <EmptyState
              title="Проектов пока нет"
              action={
                <button className="btn btn--primary" onClick={create}>
                  <Plus size={18} aria-hidden="true" />
                  Новый проект
                </button>
              }
            />
          )
        ) : (
          <ul className="proj-list sheet">
            {list.map((p) => (
              <ProjectRow key={p.id} project={p} onEdit={() => setForm({ project: p })} />
            ))}
          </ul>
        )}
      </div>

      {form && <ProjectsFormModal key={form.project?.id ?? "new"} project={form.project} onClose={() => setForm(null)} />}
    </div>
  );
}

/* ================= Строка проекта ================= */
function ProjectRow({ project: p, onEdit }: { project: Project; onEdit: () => void }) {
  const nav = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const refresh = useRefreshPosts();
  const [busy, setBusy] = useState<"" | "post" | "archive" | "delete">("");
  const [open, setOpen] = useState(false);
  const [clamped, setClamped] = useState(false);
  const rowRef = useRef<HTMLLIElement>(null);
  const excerpt = useMemo(() => projectsExcerpt(p.description), [p.description]);
  const structured = useMemo(() => projectsHasStructure(p.description), [p.description]);
  const expandable = clamped || structured || !!p.style_notes.trim();

  // показываем «Показать полностью», только если что-то действительно обрезано
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row || open) return;
    const measure = () => {
      const cut = Array.from(row.querySelectorAll<HTMLElement>("[data-clamp]")).some((el) => el.scrollHeight > el.clientHeight + 1);
      setClamped(cut);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(row);
    return () => ro.disconnect();
  }, [open, p.description, p.footer]);

  const newPost = async () => {
    setBusy("post");
    try {
      const post = await api.post<PostFull>("/api/posts", { project_id: p.id });
      qc.setQueryData(["post", post.id], post);
      refresh();
      nav(`/posts/${post.id}`);
    } catch (e) {
      toast(errText(e), "error");
      setBusy("");
    }
  };

  const toggleArchive = async () => {
    setBusy("archive");
    try {
      await api.put<Project>(`/api/projects/${p.id}`, { archived: !p.archived });
      await qc.invalidateQueries({ queryKey: ["projects"] });
      if (p.archived) toast(`Проект «${p.name}» возвращён из архива`, "ok", { label: "К активным", to: "/projects" });
      else toast(`Проект «${p.name}» отправлен в архив`, "ok", { label: "Открыть архив", to: "/projects?tab=archive" });
    } catch (e) {
      toast(errText(e), "error");
      setBusy("");
    }
  };

  const remove = async () => {
    const ok = await confirm({
      title: `Удалить проект «${p.name}»?`,
      text: (
        <>
          <p>
            {p.posts_count > 0
              ? `Его посты (${countText(p.posts_count, "пост", "поста", "постов")}) останутся на месте, только без проекта.`
              : "Постов у проекта нет."}{" "}
            Описание, рубрика, плашка и хэштеги удалятся насовсем.
          </p>
          {p.scheduled_count > 0 && (
            <p className="proj-confirm-note">Сначала снимите с публикации или перенесите его запланированные посты.</p>
          )}
        </>
      ),
      confirm: "Удалить проект",
      danger: true,
    });
    if (!ok) return;
    setBusy("delete");
    try {
      await api.del(`/api/projects/${p.id}`);
      toast(`Проект «${p.name}» удалён`);
      refresh();
    } catch (e) {
      const scheduled = e instanceof ApiError && e.status === 400 && p.scheduled_count > 0;
      toast(errText(e), "error", scheduled ? { label: "Календарь", to: "/calendar" } : undefined);
      setBusy("");
    }
  };

  const menuItems = p.archived
    ? [{ label: "Удалить", icon: Trash2, onSelect: () => void remove(), danger: true }]
    : [
        { label: "В архив", icon: Archive, onSelect: () => void toggleArchive(), disabled: p.is_general },
        { label: "Удалить", icon: Trash2, onSelect: () => void remove(), danger: true, disabled: p.is_general },
      ];

  return (
    <li
      className={`proj-row ${p.is_general ? "proj-row--general" : ""} ${busy ? "is-busy" : ""}`}
      ref={rowRef}
      aria-busy={!!busy || undefined}
    >
      <div className="proj-row__main">
        <div className="proj-row__title">
          <h3 className="proj-row__name">{p.name}</h3>
          {p.is_general && (
            <span className="chip proj-row__mark">
              <Sparkles size={14} strokeWidth={1.9} aria-hidden="true" />
              Сюда попадают автопосты
            </span>
          )}
        </div>

        {p.rubric.trim() ? (
          <p className="proj-row__rubric">
            <ProjectsVkText text={p.rubric} />
          </p>
        ) : (
          <p className="proj-row__rubric proj-row__rubric--none">Без рубрики</p>
        )}

        {p.description.trim() ? (
          open ? (
            <ProjectsRichText text={p.description} />
          ) : (
            <p className="proj-row__desc" data-clamp>
              <ProjectsVkText text={excerpt} />
            </p>
          )
        ) : (
          <p className="proj-row__desc proj-row__desc--none">Описания нет</p>
        )}

        <dl className={`proj-row__meta ${open ? "is-open" : ""}`}>
          <div>
            <dt>Плашка</dt>
            <dd data-clamp={open ? undefined : ""}>
              {p.footer.trim() ? <ProjectsVkText text={p.footer} /> : <span className="proj-none">нет</span>}
            </dd>
          </div>
          <div>
            <dt>Хэштеги</dt>
            <dd>{p.hashtags.trim() ? p.hashtags : <span className="proj-none">нет</span>}</dd>
          </div>
          {open && p.style_notes.trim() && (
            <div>
              <dt>Стиль</dt>
              <dd className="proj-row__style">{p.style_notes}</dd>
            </div>
          )}
        </dl>

        {expandable && (
          <button className="proj-row__more" aria-expanded={open} onClick={() => setOpen((x) => !x)}>
            {open ? "Свернуть" : "Показать полностью"}
            <ChevronDown size={16} aria-hidden="true" />
          </button>
        )}
      </div>

      <ul className="proj-stats" aria-label="Посты проекта">
        <Stat n={p.drafts_count} label="черновики" />
        <Stat n={p.scheduled_count} label="запланировано" />
        <Stat n={p.published_count} label="опубликовано" />
      </ul>

      <div className="proj-row__actions">
        {p.archived ? (
          <button className="btn btn--small proj-row__primary" onClick={() => void toggleArchive()} disabled={!!busy}>
            <ArchiveRestore size={16} aria-hidden="true" />
            {busy === "archive" ? "Возвращаю…" : "Вернуть из архива"}
          </button>
        ) : (
          <button className="btn btn--small proj-row__primary" onClick={() => void newPost()} disabled={!!busy}>
            <PenLine size={16} aria-hidden="true" />
            {busy === "post" ? "Создаю…" : "Новый пост"}
          </button>
        )}
        <button className="btn btn--ghost btn--small" onClick={onEdit} disabled={!!busy}>
          <Pencil size={16} aria-hidden="true" />
          Изменить
        </button>
        <ProjectsMenu
          label={`Ещё действия: ${p.name}`}
          items={menuItems}
          note={p.is_general ? "Общий проект нельзя архивировать или удалить." : undefined}
        />
      </div>
    </li>
  );
}

function Stat({ n, label }: { n: number; label: string }) {
  return (
    <li className={`proj-stat ${n === 0 ? "is-zero" : ""}`}>
      <span className="proj-stat__n">{n}</span>
      <span className="proj-stat__label">{label}</span>
    </li>
  );
}
